import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, FileText, Scale, Users, Globe, Database, Bell, Trash2, Download, CheckCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import FloatingDock from '@/components/layout/FloatingDock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SecurityPrivacy = () => {
  const navigate = useNavigate();
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [gdprRights, setGdprRights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statusRes, rightsRes] = await Promise.all([
          fetch(`${API_URL}/api/compliance/status`),
          fetch(`${API_URL}/api/compliance/gdpr/rights`)
        ]);
        
        setComplianceStatus(await statusRes.json());
        setGdprRights(await rightsRes.json());
      } catch (err) {
        console.error('Error fetching compliance data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Header */}
      <header className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-emerald-500" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Security & Privacy</h1>
              <p className="text-sm text-gray-500">Your data, your rights, our commitment</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 pb-32">
        {/* Trust Badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'GDPR', status: 'Implemented', color: 'emerald' },
            { label: 'CCPA', status: 'Implemented', color: 'emerald' },
            { label: 'SOC 2', status: 'Controls Ready', color: 'blue' },
            { label: 'ISO 27001', status: 'Controls Ready', color: 'blue' },
          ].map((badge, i) => (
            <Card key={i} className="p-4 text-center border-2 border-gray-200 dark:border-gray-700">
              <CheckCircle className={`w-8 h-8 mx-auto mb-2 text-${badge.color}-500`} />
              <div className="font-bold text-lg">{badge.label}</div>
              <div className={`text-sm text-${badge.color}-600 dark:text-${badge.color}-400`}>{badge.status}</div>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-xl mx-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="rights">Your Rights</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="data">Data Use</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <Card className="p-6 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-800">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-emerald-500" />
                Our Commitment to You
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                At Socyy, we believe your data belongs to you. We've built our platform with privacy 
                and security at its core, not as an afterthought. We comply with major international 
                privacy regulations and continuously work to exceed industry standards.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                  <Lock className="w-8 h-8 text-emerald-500 mb-2" />
                  <h3 className="font-semibold">Encrypted</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">All data encrypted at rest and in transit</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                  <Eye className="w-8 h-8 text-emerald-500 mb-2" />
                  <h3 className="font-semibold">Transparent</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Clear about what we collect and why</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                  <Users className="w-8 h-8 text-emerald-500 mb-2" />
                  <h3 className="font-semibold">Human First</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">AI assists, never replaces people</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4">Compliance Frameworks</h2>
              <div className="space-y-4">
                {complianceStatus?.frameworks && Object.entries(complianceStatus.frameworks).map(([key, value]) => (
                  <div key={key} className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className={`w-3 h-3 rounded-full mt-1.5 ${
                      value?.status === 'implemented' ? 'bg-emerald-500' : 
                      value?.status === 'controls_implemented' ? 'bg-blue-500' : 'bg-gray-400'
                    }`} />
                    <div className="flex-1">
                      <h3 className="font-semibold uppercase">{key}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {typeof value === 'object' ? (value?.status || 'N/A') : value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Your Rights Tab */}
          <TabsContent value="rights" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Scale className="w-6 h-6 text-blue-500" />
                Your Data Rights (GDPR)
              </h2>
              <div className="space-y-4">
                {gdprRights?.your_rights?.map((right, i) => (
                  <div key={i} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-lg">{right.right}</h3>
                      <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                        {right.article}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">{right.description}</p>
                    <p className="text-sm text-gray-500">
                      <strong>How to exercise:</strong> {right.how_to_exercise}
                    </p>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <h2 className="text-lg font-bold mb-4">Exercise Your Rights</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Button variant="outline" className="justify-start" onClick={() => navigate('/settings')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export My Data
                </Button>
                <Button variant="outline" className="justify-start" onClick={() => navigate('/settings')}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete My Account
                </Button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                Response time: Within 30 days as required by GDPR Article 12
              </p>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Lock className="w-6 h-6 text-purple-500" />
                Security Controls
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {complianceStatus?.security_controls && Object.entries(complianceStatus.security_controls).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <CheckCircle className={`w-5 h-5 ${value === true || value ? 'text-emerald-500' : 'text-gray-400'}`} />
                    <div>
                      <div className="font-medium capitalize">{key.replace(/_/g, ' ')}</div>
                      <div className="text-sm text-gray-500">{typeof value === 'string' ? value : (value ? 'Enabled' : 'Disabled')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-bold mb-4">Security Measures</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <span><strong>AES-256 Encryption:</strong> All sensitive data encrypted at rest</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <span><strong>TLS 1.3:</strong> All data encrypted in transit</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <span><strong>Audit Logging:</strong> Complete trail of all security events</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <span><strong>Breach Detection:</strong> Real-time monitoring for security threats</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                  <span><strong>72-Hour Notification:</strong> GDPR-compliant breach notification</span>
                </li>
              </ul>
            </Card>
          </TabsContent>

          {/* Data Use Tab */}
          <TabsContent value="data" className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Database className="w-6 h-6 text-orange-500" />
                What Data We Collect
              </h2>
              <div className="space-y-4">
                {[
                  { category: 'Account Information', items: 'Name, email, username', purpose: 'Account management' },
                  { category: 'Content', items: 'Posts, comments, messages', purpose: 'Service delivery' },
                  { category: 'Usage Data', items: 'Interactions, preferences', purpose: 'Service improvement' },
                  { category: 'Device Info', items: 'IP address, browser type', purpose: 'Security & analytics' },
                ].map((item, i) => (
                  <div key={i} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="font-semibold">{item.category}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{item.items}</p>
                    <p className="text-xs text-gray-500 mt-1">Purpose: {item.purpose}</p>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800">
              <h2 className="text-lg font-bold mb-4">We Do NOT:</h2>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <span className="text-red-500">✗</span>
                  Sell your personal data to third parties
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-500">✗</span>
                  Use your data for AI training without consent
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-500">✗</span>
                  Share data with advertisers without permission
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-500">✗</span>
                  Store data longer than necessary
                </li>
              </ul>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Contact */}
        <Card className="p-6 mt-8 text-center">
          <h2 className="text-lg font-bold mb-2">Questions or Concerns?</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Our Data Protection team is here to help
          </p>
          <div className="flex justify-center gap-4">
            <Button variant="outline">
              <Bell className="w-4 h-4 mr-2" />
              privacy@socyy.com
            </Button>
            <Button variant="outline">
              <Shield className="w-4 h-4 mr-2" />
              security@socyy.com
            </Button>
          </div>
        </Card>
      </main>

      <FloatingDock />
    </div>
  );
};

export default SecurityPrivacy;
